import { createPortal } from "react-dom";
import ScheduledMeetingActions from "./ScheduledMeetingActions";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import CandidateScheduleDialog from "./CandidateScheduleDialog";
import { schedulingRequest } from "../utils/interviewScheduling";

export default function DiscoveryScheduling({ leadId, status, onSaved, actionTarget, contactName = "", showMeetings = true }) {
  const [data, setData] = useState(null);
  const [editor, setEditor] = useState(null);
  const [opening, setOpening] = useState(false);
  const contextPath = `/calendar/discovery-calls?${new URLSearchParams({ leadId: String(leadId), contactName })}`;
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    const payload = await schedulingRequest(contextPath);
    setData(payload);
  }, [contextPath]);
  useEffect(() => {
    if (!leadId) return undefined;
    let active = true;
    const refresh = () => schedulingRequest(contextPath)
      .then(payload => { if (active) setData(payload); }).catch(err => { if (active) setError(err.message); });
    setData(null); setEditor(null); setError(""); refresh();
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [contextPath, leadId]);
  async function openScheduler() {
    setOpening(true); setError("");
    try {
      const payload = await schedulingRequest(contextPath);
      setData(payload);
      const context = payload.context || {};
      const matchingContact = context.interviewers?.find(person =>
        person.name?.trim().toLowerCase() === contactName.trim().toLowerCase());
      let contact = context.contact?.name ? context.contact : contactName.trim()
        ? matchingContact || { name: contactName.trim(), email: "" } : context.interviewers?.[0];
      if (contact?.name && !contact.id && contact.kind !== "lead_contact") {
        const normalize = value => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
        const selected = context.interviewers?.find(person => person.id && normalize(person.name) === normalize(contact.name));
        if (selected) contact = selected;
      }
      if (contact?.name && !contact.id && contact.kind !== "lead_contact") {
        const result = await schedulingRequest(`/hiring-managers?${new URLSearchParams({ search: contact.name })}`);
        const normalize = value => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
        const matches = (Array.isArray(result) ? result : result.rows || [])
          .filter(person => normalize(person.name) === normalize(contact.name));
        if (matches.length === 1) contact = matches[0];
      }
      setEditor({ context: { ...context, kind: "discovery", leadId, contact,
        interviewers: contact ? [contact, ...(context.interviewers || []).slice(1)] : [] } });
    } catch (err) { setError(err.message); }
    finally { setOpening(false); }
  }
  const scheduleAction = leadId && String(status).toLowerCase() === "discovery" ? (
    <Button variant="contained" startIcon={<EventOutlinedIcon />} disabled={!data || opening} onClick={openScheduler}
      sx={{ whiteSpace: "nowrap" }}>Schedule Discovery Call</Button>
  ) : null;
  const showDetails = error || data?.syncError || (showMeetings && data?.calls?.length) || (actionTarget === undefined && scheduleAction);
  return <>
    {actionTarget && scheduleAction ? createPortal(scheduleAction, actionTarget) : null}
    {showDetails ? <Box sx={{ mx: 2.5, mb: 2 }}>
    {error && <Alert severity="error">{error}</Alert>}
    {data?.syncError && <Alert severity="warning">{data.syncError}</Alert>}
    {actionTarget === undefined && scheduleAction}
    {showMeetings && data?.calls?.map(call => <Stack key={call.id} direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
      <Typography variant="body2">{call.event_title} - {new Date(call.starts_at).toLocaleString()} - {call.interview_status}</Typography>
      {call.calendar_url && <Button href={call.calendar_url} target="_blank" rel="noopener noreferrer">View in Calendar</Button>}
      <ScheduledMeetingActions record={call} onSaved={() => { load().catch(err => setError(err.message)); return onSaved?.(); }} />
    </Stack>)}
    </Box> : null}
    {editor && <CandidateScheduleDialog open context={editor.context} initialEvent={editor.id ? editor : null} onClose={() => setEditor(null)} onSaved={() => { load().catch(err => setError(err.message)); Promise.resolve(onSaved?.()).catch(err => setError(err.message)); }} />}
  </>;
}
